import { Decorative } from "@faire/design-tokens";
import { PaddedContainer } from "@faire/retailer-visitor-shared/features/Marketplace/style";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import {
  useAssignSettingCategorySuppliersPageCTA,
  useSettingCategorySuppliersPageCTA,
} from "@faire/retailer-visitor-shared/settings/useSettingCategorySuppliersPageCTA";
import { useLoggedOutSuppliersPageCurrentCategory } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/Context/useLoggedOutSuppliersPageCurrentCategory";
import { useIsLoggedOutSuppliersLayoutElementLoading } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/DataFetching/LoggedOutSuppliersLayoutElement/useIsLoggedOutSuppliersLayoutElementLoading";
import { LoggedOutSuppliersPageBreadcrumb } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/Components/LoggedOutSuppliersPageHeader/__internal__/LoggedOutSuppliersPageBreadcrumb/LoggedOutSuppliersPageBreadcrumb";
import { LoggedOutSuppliersPageHeaderCategoryCarousel } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/Components/LoggedOutSuppliersPageHeader/__internal__/LoggedOutSuppliersPageHeaderCategoryCarousel";
import { useHeaderCarouselCategoriesToDisplay } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/useHeaderCarouselCategoriesToDisplay";
import { SuppliersPageHeaderLoadingSkeleton } from "@faire/retailer-visitor-shared/views/suppliers/SuppliersPageLoadingSkeleton/SuppliersPageHeaderLoadingSkeleton";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Lock } from "@faire/web--source/slate/icons/foundation/Lock";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import {
  Typography,
  TypographyStyles,
} from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCategoriesGetStartedCtaClick } from "@faire/web-api--source/events/categories/click/getStartedCta";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

interface IProps {
  title: string | undefined;
  subTitle: string | undefined;
}

export const LoggedOutSuppliersPageHeader: React.FC<IProps> = ({
  title,
  subTitle,
}) => {
  const { isTabletAndBelow } = useViewport();
  const history = useHistory();
  const categoryModel = useLoggedOutSuppliersPageCurrentCategory();

  const isLoading = useIsLoggedOutSuppliersLayoutElementLoading();
  const categoriesToDisplay = useHeaderCarouselCategoriesToDisplay();
  const shouldShowCarousel = !!categoriesToDisplay.length;

  const shouldShowCategorySuppliersPageCTA =
    useSettingCategorySuppliersPageCTA();
  const assignSettingCategorySuppliersPageCTA =
    useAssignSettingCategorySuppliersPageCTA();
  useEffect(() => {
    assignSettingCategorySuppliersPageCTA();
  }, [assignSettingCategorySuppliersPageCTA]);

  const renderDetailsSection = () => {
    if (isLoading) {
      return <SuppliersPageHeaderLoadingSkeleton />;
    }
    return (
      <DetailsContainer $isCarouselShown={shouldShowCarousel}>
        <TitleH1 $isCarouselShown={shouldShowCarousel}>{title}</TitleH1>
        <Spacer height="1x" />
        <TitleH2>{subTitle}</TitleH2>
        <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "3x" }} />
        <CTAButton
          variant="tertiary"
          onClick={() => {
            trackCategoriesGetStartedCtaClick(
              categoryModel.mostSpecificPathIdentifier ?? ""
            );
            popSignUp(history, SignUpType.GENERIC, () => {}, {
              element: SignUpElement.HERO,
              sourceId: "suppliers_page_hero_signup_cta",
            });
          }}
        >
          {shouldShowCategorySuppliersPageCTA ? (
            <>
              <Lock lockType="foundation" fontSize={16} />
              <Spacer width={4} />
              <StrictLocalMsg
                defaultMessage="Unlock wholesale pricing"
                description={{
                  text: "sign up cta for discover page header",
                }}
              />
            </>
          ) : (
            <CTATypography variant="subheadingSansRegular">
              <StrictLocalMsg
                defaultMessage="Get started for free"
                description={{
                  text: "Call to action line to prompt people to sign up",
                  img: "https://cdn.faire.com/fastly/0d8743b2307966dd5833acbb19b226f2a9470a3087bc3193fce616b3f85d38ad.png",
                }}
              />
              <LeftMarginArrow direction="right" />
            </CTATypography>
          )}
        </CTAButton>
      </DetailsContainer>
    );
  };

  const renderDesktop = () => {
    return (
      <PaddedContainer>
        <LoggedOutSuppliersPageBreadcrumb category={categoryModel} />
        <Spacer height="2x" />
        <HeaderRow align="start">
          {renderDetailsSection()}
          {shouldShowCarousel ? (
            <CenterItemRow data-test-id="logged-out-suppliers-page-header-carousel">
              <Spacer width="5x" />
              <LoggedOutSuppliersPageHeaderCategoryCarousel
                categoryPathIdentifier={
                  categoryModel.mostSpecificPathIdentifier
                }
              />
            </CenterItemRow>
          ) : null}
        </HeaderRow>
      </PaddedContainer>
    );
  };

  const renderTabletAndBelow = () => {
    return (
      <Column>
        <PaddedContainer>
          <LoggedOutSuppliersPageBreadcrumb category={categoryModel} />
          <Spacer height="1x" />
          {renderDetailsSection()}
        </PaddedContainer>
      </Column>
    );
  };

  return (
    <HeaderWrapper>
      {isTabletAndBelow ? renderTabletAndBelow() : renderDesktop()}
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled(Column)`
  width: 100%;
  background-color: ${Decorative.tint.neutral};
  padding: ${getSpacing("4x", 0, "5x", 0)};
`;

// For SEO purpose, we have to make this h1 tag, please don't change unless
// consulted with the SEO team
const TitleH1 = styled.h1<{ $isCarouselShown?: boolean }>`
  width: 100%;
  word-break: break-word;
  ${TypographyStyles({ variant: "displaySSerifRegular" })}
  ${responsiveMediaQueries.desktopAndAbove} {
    ${TypographyStyles({ variant: "displayMSerifRegular" })}
    width: ${({ $isCarouselShown }) => ($isCarouselShown ? "340px" : "100%")};
  }
`;

// For SEO purpose, we have to make this h2 tag, please don't change unless
// consulted with the SEO team
const TitleH2 = styled.h2`
  word-break: break-word;
  ${TypographyStyles({ variant: "subheadingSansRegular" })}
`;

const LeftMarginArrow = styled(Arrow)`
  margin-left: 4px;
`;

const CTAButton = styled(Button)`
  align-self: start;
`;

const CTATypography = styled(Typography)`
  display: flex;
  align-items: center;
`;

const CenterItemRow = styled(Row).attrs({ align: "center" })`
  width: 100%;
`;

const HeaderRow = styled(Row)`
  width: 100%;
`;

const DetailsContainer = styled(Column)<{ $isCarouselShown?: boolean }>`
  width: 100%;
  ${responsiveMediaQueries.desktopAndAbove} {
    width: ${({ $isCarouselShown }) => ($isCarouselShown ? "340px" : "100%")};
  }
`;
